import { CONCEPT_ICONS } from "@/components/ui/concept-icons";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";
import { Icons } from "@/components/ui/icons";
import { Button } from "@/components/ui/button";
import { SidebarFrameItem } from "@/components/panels/sidebar-frame-item";
import { FrameContextMenu } from "@/components/shapes/shared/frame-context-menu";
import { BrowseTree } from "@/components/panels/browse-tree";
import type { BrowseProject } from "@/components/panels/browse-tree";
import { SidebarTaskItem } from "@/components/panels/sidebar-task-item";
import { ExecutionSidebar } from "@/components/panels/execution-sidebar";
import type { ExecutionSidebarProps } from "@/components/panels/execution-sidebar";
import { InlineEditText } from "@/components/ui/inline-edit-text";
import {
  surfaceHoverClassName,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui/surface-contract";

/**
 * Left sidebar panel — 240px wide, full-height scrollable panel.
 *
 * .pen source: component/left-sidebar (ZtCrd) — 240x852
 *   - bg $panel-fill, border-right $canvas-border, backdrop-blur-glass
 *   - Tabs: "Frames" (frame list) | "Browse" (tree view)
 *   - Tasks section: always visible at the bottom, separated by top border
 */

type SidebarTab = "frames" | "browse";

/** Data shape for a frame displayed in the sidebar */
interface FrameEntry {
  id: string;
  title: string;
  icon: LucideIcon;
  accentColor?: string;
  frameType?: string;
  active?: boolean;
  statusColor?: string;
  hidden?: boolean;
}

/** Data shape for a task displayed in the sidebar */
interface TaskEntry {
  id: string;
  title: string;
  checked?: boolean;
  status?: string;
  agentLetter?: string;
  agentColor?: string;
}

interface LeftSidebarProps {
  /** Render the desktop-style integrated header above the sidebar tabs. */
  showHeader?: boolean;
  /** Toggle sidebar visibility */
  onToggle?: () => void;
  /** Current project name shown in the integrated sidebar header */
  projectName?: string;
  /** Current space name shown in the integrated sidebar header */
  spaceName?: string;
  /** Callback to rename the current project */
  onRenameProject?: (name: string) => void;
  /** Callback to rename the current space */
  onRenameSpace?: (name: string) => void;
  /** Whether the current space is the onboarding space */
  isOnboarding?: boolean;
  /** Callback to replay the onboarding tour */
  onReplayTour?: () => void;
  /** Which tab is active */
  activeTab?: SidebarTab;
  /** List of frame entries to display */
  frames?: FrameEntry[];
  /** List of task entries (always visible at bottom) */
  tasks?: TaskEntry[];
  /** Callback when tab changes */
  onTabChange?: (tab: SidebarTab) => void;
  /** Callback when add-frame button is clicked */
  onAddFrame?: () => void;
  /** Callback when a frame item is clicked */
  onSelectFrame?: (frameId: string) => void;
  /** Callback to toggle frame visibility (eye icon) */
  onToggleFrameVisibility?: (frameId: string) => void;
  /** Browse-tree: list of projects with nested spaces */
  browseProjects?: BrowseProject[];
  /** Browse-tree callbacks */
  onBrowseSelectProject?: (projectId: string) => void;
  onBrowseSelectSpace?: (projectId: string, spaceId: string) => void;
  onBrowseToggleProject?: (projectId: string) => void;
  onBrowseNewProject?: () => void;
  onBrowseRenameProject?: (projectId: string, name: string) => void;
  onBrowseSetColorProject?: (projectId: string, color: string) => void;
  onBrowsePinProject?: (projectId: string) => void;
  onBrowseProjectSettings?: (projectId: string) => void;
  onBrowseNewSpace?: (projectId: string) => void;
  onBrowseDeleteProject?: (projectId: string) => void;
  onBrowseRenameSpace?: (
    projectId: string,
    spaceId: string,
    name: string,
  ) => void;
  onBrowseSetColorSpace?: (
    projectId: string,
    spaceId: string,
    color: string,
  ) => void;
  onBrowsePinSpace?: (projectId: string, spaceId: string) => void;
  onBrowseOrganizeFrames?: (projectId: string, spaceId: string) => void;
  onBrowseMoveSpace?: (
    fromProjectId: string,
    spaceId: string,
    toProjectId: string,
  ) => void;
  onBrowseDeleteSpace?: (projectId: string, spaceId: string) => void;
  /** Callback when a task item is clicked */
  onSelectTask?: (taskId: string) => void;
  /** Callback when a task checkbox is toggled */
  onToggleTask?: (taskId: string) => void;
  /** Callback when add-task button is clicked */
  onAddTask?: () => void;
  /** When set, replaces the Tasks section with the execution progress view. */
  execution?: ExecutionSidebarProps;
  className?: string;
  "data-testid"?: string;
}

/** Internal tab button sub-component */
function SidebarTabButton({
  label,
  icon,
  isActive,
  onClick,
  testId,
}: {
  label: string;
  icon: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      variant="ghost"
      size="tab"
      type="button"
      className={cn(
        surfaceHoverClassName(),
        "flex flex-1 items-center justify-center border border-transparent bg-transparent font-semibold",
        isActive
          ? "bg-glass-stroke-subtle text-primary"
          : "text-muted hover:text-secondary",
      )}
      onClick={onClick}
      data-testid={testId}
      aria-selected={isActive}
      role="tab"
    >
      {icon}
      {label}
    </Button>
  );
}

function SidebarTitle({
  projectName,
  spaceName,
  onRenameProject,
  onRenameSpace,
}: {
  projectName?: string;
  spaceName?: string;
  onRenameProject?: (name: string) => void;
  onRenameSpace?: (name: string) => void;
}) {
  if (!projectName && !spaceName) {
    return (
      <span className="text-appkit-subhead font-bold text-accent">Denker</span>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-[calc(var(--appkit-space-2xs)/2)]">
      {projectName &&
        (onRenameProject ? (
          <InlineEditText
            value={projectName}
            onSave={onRenameProject}
            maxLength={50}
            className="truncate text-appkit-caption font-semibold"
            data-testid="sidebar-project-name"
          />
        ) : (
          <span className="truncate text-appkit-caption font-semibold text-primary">
            {projectName}
          </span>
        ))}
      {spaceName &&
        (onRenameSpace ? (
          <InlineEditText
            value={spaceName}
            onSave={onRenameSpace}
            maxLength={50}
            className="truncate text-appkit-small font-medium"
            data-testid="sidebar-space-name"
          />
        ) : (
          <span className="truncate text-appkit-small font-medium text-secondary">
            {spaceName}
          </span>
        ))}
    </div>
  );
}

export function LeftSidebar({
  showHeader = false,
  onToggle,
  projectName,
  spaceName,
  onRenameProject,
  onRenameSpace,
  isOnboarding,
  onReplayTour,
  activeTab = "frames",
  frames = [],
  tasks = [],
  onTabChange,
  onAddFrame,
  onSelectFrame,
  onToggleFrameVisibility,
  browseProjects,
  onBrowseSelectProject,
  onBrowseSelectSpace,
  onBrowseToggleProject,
  onBrowseNewProject,
  onBrowseRenameProject,
  onBrowseSetColorProject,
  onBrowsePinProject,
  onBrowseProjectSettings,
  onBrowseNewSpace,
  onBrowseDeleteProject,
  onBrowseRenameSpace,
  onBrowseSetColorSpace,
  onBrowsePinSpace,
  onBrowseOrganizeFrames,
  onBrowseMoveSpace,
  onBrowseDeleteSpace,
  onSelectTask,
  onToggleTask,
  execution,
  className,
  "data-testid": testId = "left-sidebar",
}: LeftSidebarProps) {
  return (
    <aside
      className={cn(
        surfaceRoleClassName("side-panel"),
        "flex w-60 flex-col",
        className,
      )}
      data-testid={testId}
      {...surfaceRoleAttributes("side-panel", {
        nativeLevel: "root",
        nativeGroup: "left-panel",
      })}
    >
      {showHeader && (
        <div
          className="flex min-h-appkit-control-icon items-center gap-appkit-xs border-b border-glass-stroke-subtle px-appkit-sm py-appkit-xs"
          data-testid="left-sidebar-header"
        >
          <SidebarTitle
            projectName={projectName}
            spaceName={spaceName}
            onRenameProject={onRenameProject}
            onRenameSpace={onRenameSpace}
          />
          {isOnboarding && onReplayTour && (
            <Button
              variant="ghost"
              size="icon"
              type="button"
              className={cn(
                surfaceHoverClassName(),
                "group relative shrink-0 border border-transparent bg-transparent text-appkit-body leading-none",
              )}
              onClick={onReplayTour}
              data-testid="sidebar-replay-tour-btn"
              aria-label="Replay tour"
            >
              <span aria-hidden="true">🎉</span>
              <span className="denker-glass-tooltip pointer-events-none absolute -bottom-7 left-1/2 z-50 -translate-x-1/2 whitespace-nowrap rounded-control-sm border px-appkit-2xs py-appkit-2xs text-appkit-mini opacity-0 transition-opacity group-hover:opacity-100">
                Replay tour
              </span>
            </Button>
          )}
          {onToggle && (
            <Button
              variant="ghost"
              size="icon"
              className="shrink-0"
              onClick={onToggle}
              data-testid="left-sidebar-toggle"
              aria-label="Hide sidebar"
            >
              <Icons.PanelLeft className="h-appkit-md w-appkit-md" />
            </Button>
          )}
        </div>
      )}

      {/* Sidebar tabs */}
      <div
        className="flex items-center gap-appkit-2xs px-appkit-xs py-appkit-xs"
        role="tablist"
        aria-label="Sidebar tabs"
      >
        <SidebarTabButton
          label="Frames"
          icon={<CONCEPT_ICONS.frame className="h-appkit-sm w-appkit-sm" />}
          isActive={activeTab === "frames"}
          onClick={() => onTabChange?.("frames")}
          testId="sidebar-tab-frames"
        />
        <SidebarTabButton
          label="Browse"
          icon={<CONCEPT_ICONS.browse className="h-appkit-sm w-appkit-sm" />}
          isActive={activeTab === "browse"}
          onClick={() => onTabChange?.("browse")}
          testId="sidebar-tab-browse"
        />
      </div>

      {/* Scrollable content area */}
      <div
        className="scrollbar-none flex-1 overflow-y-auto"
        data-testid="sidebar-scroll-content"
      >
        {activeTab === "frames" && (
          <div className="flex flex-col" data-testid="sidebar-frame-list">
            {/* Frame list header with add button */}
            <div
              className="flex items-center justify-between px-appkit-sm py-appkit-2xs"
              data-testid="sidebar-frame-list-header"
            >
              <span className="text-appkit-mini font-medium uppercase tracking-wider text-muted">
                Frames
              </span>
              <Button
                variant="ghost"
                size="iconSm"
                className={surfaceHoverClassName()}
                onClick={onAddFrame}
                data-testid="sidebar-add-frame"
                aria-label="Add frame"
              >
                <Icons.Plus className="h-appkit-sm w-appkit-sm" />
              </Button>
            </div>

            {/* Frame items */}
            <div className="flex flex-col">
              {frames.map((frame) => (
                <FrameContextMenu key={frame.id} frameId={frame.id}>
                  <SidebarFrameItem
                    title={frame.title}
                    icon={frame.icon}
                    active={frame.active}
                    statusColor={frame.statusColor}
                    hidden={frame.hidden}
                    onToggleVisibility={() =>
                      onToggleFrameVisibility?.(frame.id)
                    }
                    onClick={() => onSelectFrame?.(frame.id)}
                    data-testid={`sidebar-frame-${frame.id}`}
                  />
                </FrameContextMenu>
              ))}
              {frames.length === 0 && (
                <p className="px-appkit-md py-appkit-sm text-center text-appkit-mini text-muted">
                  No frames yet
                </p>
              )}
            </div>
          </div>
        )}

        {activeTab === "browse" && (
          <BrowseTree
            projects={browseProjects ?? []}
            onSelectProject={onBrowseSelectProject}
            onSelectSpace={onBrowseSelectSpace}
            onToggleProject={onBrowseToggleProject}
            onNewProject={onBrowseNewProject}
            onRenameProject={onBrowseRenameProject}
            onSetColorProject={onBrowseSetColorProject}
            onPinProject={onBrowsePinProject}
            onProjectSettings={onBrowseProjectSettings}
            onNewSpace={onBrowseNewSpace}
            onDeleteProject={onBrowseDeleteProject}
            onRenameSpace={onBrowseRenameSpace}
            onSetColorSpace={onBrowseSetColorSpace}
            onPinSpace={onBrowsePinSpace}
            onOrganizeFrames={onBrowseOrganizeFrames}
            onMoveSpace={onBrowseMoveSpace}
            onDeleteSpace={onBrowseDeleteSpace}
          />
        )}
      </div>

      {/* Bottom section — execution sidebar or tasks */}
      <div className="shrink-0" data-testid="sidebar-bottom-section">
        {execution ? (
          <div
            className="scrollbar-none max-h-[calc(var(--appkit-space-xl)*8)] overflow-auto"
            data-testid="sidebar-execution-section"
          >
            <ExecutionSidebar {...execution} />
          </div>
        ) : (
          <>
            {/* Tasks header: green dot + label + count + add button */}
            <div
              className="flex items-center gap-appkit-2xs px-appkit-md py-appkit-2xs"
              data-testid="sidebar-tasks-header"
            >
              <span className="h-[calc(var(--appkit-space-2xs)*1.5)] w-[calc(var(--appkit-space-2xs)*1.5)] shrink-0 rounded-pill bg-accent" />
              <span className="text-appkit-small font-semibold text-secondary">
                Tasks
              </span>
              <span className="flex-1" />
              {tasks.length > 0 && (
                <span className="text-appkit-mini font-medium text-muted">
                  {tasks.length}
                </span>
              )}
            </div>

            {/* Task items */}
            <div
              className="scrollbar-none flex max-h-[calc(var(--appkit-space-xl)*5)] flex-col overflow-y-auto"
              data-testid="sidebar-task-list"
            >
              {tasks.map((task) => (
                <SidebarTaskItem
                  key={task.id}
                  title={task.title}
                  checked={task.checked}
                  status={task.status}
                  agentLetter={task.agentLetter}
                  agentColor={task.agentColor}
                  onToggle={() => onToggleTask?.(task.id)}
                  onClick={() => onSelectTask?.(task.id)}
                  data-testid={`sidebar-task-${task.id}`}
                />
              ))}
              {tasks.length === 0 && (
                <p className="px-appkit-md py-appkit-xs text-center text-appkit-mini text-muted">
                  No tasks yet
                </p>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
}

export type { LeftSidebarProps, SidebarTab, FrameEntry, TaskEntry };
